import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { queryJobs } from '@/features/jobs/data/jobs';
import {
  buildCountedJobFilterOptions,
  filterJobsBySearch,
} from '@/features/jobs/hooks/useJobsPageViewModel.helpers';
import { fetchAllQueryPages } from '@/shared/data/fetchAllQueryPages';
import { queryKeys } from '@/shared/hooks';
import {
  buildDateRangeFilters,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type { MetricsToolbarTagOption } from '@/features/shared/components/metricsToolbar/metricsToolbarTagOptions';
import type { TableDropdownOption } from '@/features/shared/components/tableSystem/tableToolbar.types';
import type { BuildStatus, JobListItemDto } from '@flowqa/shared';

type UseJobsPageFilterOptionCountsArgs = {
  currentJobs: readonly JobListItemDto[];
  effectiveProjectId: string | null;
  isAuthenticated: boolean;
  jobOptions: readonly TableDropdownOption[];
  jobSearchValue: string;
  pipelineOptions: readonly TableDropdownOption[];
  rangeEndIso: string | null;
  rangeStartIso: string | null;
  selectedEndDay: string | null;
  selectedJobNames: string[];
  selectedOrganisationId: string | null;
  selectedPipelineIds: string[];
  selectedStageNames: string[];
  selectedStartDay: string | null;
  selectedStatuses: BuildStatus[];
  selectedTags: string[];
  stageOptions: readonly TableDropdownOption[];
  tagOptions: readonly MetricsToolbarTagOption[];
};

type CountedJobFilterOptions = ReturnType<typeof buildCountedJobFilterOptions>;

export const useJobsPageFilterOptionCounts = ({
  currentJobs,
  effectiveProjectId,
  isAuthenticated,
  jobOptions,
  jobSearchValue,
  pipelineOptions,
  rangeEndIso,
  rangeStartIso,
  selectedEndDay,
  selectedJobNames,
  selectedOrganisationId,
  selectedPipelineIds,
  selectedStageNames,
  selectedStartDay,
  selectedStatuses,
  selectedTags,
  stageOptions,
  tagOptions,
}: UseJobsPageFilterOptionCountsArgs): CountedJobFilterOptions => {
  const jobCountDateFilters = useMemo(
    () =>
      buildDateRangeFilters(
        'startedAt',
        selectedStartDay ? dayjs(selectedStartDay).startOf('day').toISOString() : rangeStartIso,
        selectedEndDay ? dayjs(selectedEndDay).endOf('day').toISOString() : rangeEndIso,
      ),
    [rangeEndIso, rangeStartIso, selectedEndDay, selectedStartDay],
  );
  const pipelineCountJobFilters = useMemo(
    () =>
      mergeFilters(
        buildInFilter('status', selectedStatuses),
        buildInFilter('stageName', selectedStageNames),
        buildInFilter('jobName', selectedJobNames),
        buildInFilter('tags', selectedTags),
        jobCountDateFilters,
      ),
    [jobCountDateFilters, selectedJobNames, selectedStageNames, selectedStatuses, selectedTags],
  );
  const stageCountJobFilters = useMemo(
    () =>
      mergeFilters(
        buildInFilter('status', selectedStatuses),
        buildInFilter('pipelineId', selectedPipelineIds),
        buildInFilter('jobName', selectedJobNames),
        buildInFilter('tags', selectedTags),
        jobCountDateFilters,
      ),
    [jobCountDateFilters, selectedJobNames, selectedPipelineIds, selectedStatuses, selectedTags],
  );
  const jobNameCountJobFilters = useMemo(
    () =>
      mergeFilters(
        buildInFilter('status', selectedStatuses),
        buildInFilter('pipelineId', selectedPipelineIds),
        buildInFilter('stageName', selectedStageNames),
        buildInFilter('tags', selectedTags),
        jobCountDateFilters,
      ),
    [jobCountDateFilters, selectedPipelineIds, selectedStageNames, selectedStatuses, selectedTags],
  );
  const tagCountJobFilters = useMemo(
    () =>
      mergeFilters(
        buildInFilter('status', selectedStatuses),
        buildInFilter('pipelineId', selectedPipelineIds),
        buildInFilter('stageName', selectedStageNames),
        buildInFilter('jobName', selectedJobNames),
        jobCountDateFilters,
      ),
    [
      jobCountDateFilters,
      selectedJobNames,
      selectedPipelineIds,
      selectedStageNames,
      selectedStatuses,
    ],
  );
  const pipelineCountJobsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'jobs/page',
      'pipeline-counts',
      selectedOrganisationId,
      effectiveProjectId,
      jobSearchValue,
      JSON.stringify(pipelineCountJobFilters),
    ),
    enabled: isAuthenticated,
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryJobs(effectiveProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            search: jobSearchValue,
            sorts: [{ field: 'jobName', direction: 'asc' }],
            filters: pipelineCountJobFilters,
          }),
      ),
  });
  const stageCountJobsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'jobs/page',
      'stage-counts',
      selectedOrganisationId,
      effectiveProjectId,
      jobSearchValue,
      JSON.stringify(stageCountJobFilters),
    ),
    enabled: isAuthenticated,
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryJobs(effectiveProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            search: jobSearchValue,
            sorts: [{ field: 'jobName', direction: 'asc' }],
            filters: stageCountJobFilters,
          }),
      ),
  });
  const jobNameCountJobsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'jobs/page',
      'job-name-counts',
      selectedOrganisationId,
      effectiveProjectId,
      jobSearchValue,
      JSON.stringify(jobNameCountJobFilters),
    ),
    enabled: isAuthenticated,
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryJobs(effectiveProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            search: jobSearchValue,
            sorts: [{ field: 'jobName', direction: 'asc' }],
            filters: jobNameCountJobFilters,
          }),
      ),
  });
  const tagCountJobsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'jobs/page',
      'tag-counts',
      selectedOrganisationId,
      effectiveProjectId,
      jobSearchValue,
      JSON.stringify(tagCountJobFilters),
    ),
    enabled: isAuthenticated,
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryJobs(effectiveProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            search: jobSearchValue,
            sorts: [{ field: 'jobName', direction: 'asc' }],
            filters: tagCountJobFilters,
          }),
      ),
  });

  return useMemo(
    () =>
      buildCountedJobFilterOptions({
        jobs: currentJobs,
        pipelineJobs: filterJobsBySearch(pipelineCountJobsQuery.data?.items ?? [], jobSearchValue),
        stageJobs: filterJobsBySearch(stageCountJobsQuery.data?.items ?? [], jobSearchValue),
        jobNameJobs: filterJobsBySearch(jobNameCountJobsQuery.data?.items ?? [], jobSearchValue),
        tagJobs: filterJobsBySearch(tagCountJobsQuery.data?.items ?? [], jobSearchValue),
        pipelineOptions,
        stageOptions,
        jobOptions,
        tagOptions,
      }),
    [
      currentJobs,
      jobNameCountJobsQuery.data?.items,
      jobOptions,
      jobSearchValue,
      pipelineCountJobsQuery.data?.items,
      pipelineOptions,
      stageCountJobsQuery.data?.items,
      stageOptions,
      tagCountJobsQuery.data?.items,
      tagOptions,
    ],
  );
};
